import { FiltersDropdown } from '@shared/components/ui/FiltersDropdown';
import { Search } from '@shared/components/ui/Search';
import { SimpleFilterContent, type SimpleFilterField } from '@shared/components/ui/SimpleFilter';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClientsDropdown, useCouriersDropdown, useShopsDropdown } from '../../hooks';
import { MismatchFilters, MismatchType } from '../../models';

interface FiltersSectionProps {
  filters: MismatchFilters;
  onFiltersChange: (filters: Partial<MismatchFilters>) => void;
  onResetFilters: () => void;
  searchTerm: string;
  onSearch: (value: string) => void;
}

export const FiltersSection = ({
  filters,
  onFiltersChange,
  onResetFilters,
  searchTerm,
  onSearch,
}: FiltersSectionProps) => {
  const { t } = useTranslation('codMismatches');
  const { clients, isLoading: isLoadingClients } = useClientsDropdown();
  const { shops, isLoading: isLoadingShops } = useShopsDropdown();
  const { couriers, isLoading: isLoadingCouriers } = useCouriersDropdown();

  // Estado local para el input de búsqueda
  const [inputValue, setInputValue] = useState(searchTerm);

  // Sincronizar con searchTerm externo
  useEffect(() => {
    setInputValue((prev) => (prev === searchTerm ? prev : searchTerm));
  }, [searchTerm]);

  // Handler para cambio en el input
  const handleInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  }, []);

  // Handler para ejecutar búsqueda (Enter o click)
  const handleSearchSubmit = useCallback(
    (value?: string) => {
      const searchValue = value !== undefined ? value : inputValue;
      const trimmedValue = searchValue.trim();
      onSearch(trimmedValue);
    },
    [inputValue, onSearch],
  );

  // 🎯 Configuración de campos para SimpleFilter (sin el campo de referencia)
  const fields: SimpleFilterField[] = useMemo(
    () => [
      {
        key: 'client_id',
        label: t('filters.client', 'Cliente'),
        type: 'select',
        options: clients.map((client) => ({
          label: client.name,
          value: String(client.id),
        })),
        allOption: {
          label: t('filters.all_clients', 'Todos los clientes'),
          value: 'all',
        },
        isLoadingOptions: isLoadingClients,
        placeholder: isLoadingClients
          ? t('filters.loading', 'Cargando...')
          : t('filters.select_client', 'Seleccionar cliente'),
        minWidth: '180px',
        maxWidth: '220px',
      },
      {
        key: 'shop_id',
        label: t('filters.shop', 'Tienda'),
        type: 'select',
        options: shops.map((shop) => ({
          label: shop.name,
          value: String(shop.id),
        })),
        allOption: {
          label: t('filters.all_shops', 'Todas las tiendas'),
          value: 'all',
        },
        isLoadingOptions: isLoadingShops,
        placeholder: isLoadingShops
          ? t('filters.loading', 'Cargando...')
          : t('filters.select_shop', 'Seleccionar tienda'),
        minWidth: '180px',
        maxWidth: '220px',
      },
      {
        key: 'courier_id',
        label: t('filters.courier', 'Courier'),
        type: 'select',
        options: couriers.map((courier) => ({
          label: courier.name,
          value: String(courier.id),
        })),
        allOption: {
          label: t('filters.all_couriers', 'Todos los couriers'),
          value: 'all',
        },
        isLoadingOptions: isLoadingCouriers,
        placeholder: isLoadingCouriers
          ? t('filters.loading', 'Cargando...')
          : t('filters.select_courier', 'Seleccionar courier'),
        minWidth: '180px',
        maxWidth: '220px',
      },
      {
        key: 'tracking_number',
        label: t('filters.tracking_number', 'Tracking'),
        type: 'text',
        placeholder: t('filters.tracking_placeholder', 'Ej: TRACK123'),
        minWidth: '180px',
      },
      {
        key: 'mismatch_type',
        label: t('filters.mismatch_type', 'Tipo de Descuadre'),
        type: 'select',
        options: [
          { label: t('mismatch_types.pedido_sin_cobrar', 'Pedido sin cobrar'), value: 'pedido_sin_cobrar' },
          { label: t('mismatch_types.pedido_sin_pagar', 'Pedido sin pagar'), value: 'pedido_sin_pagar' },
          { label: t('mismatch_types.courier_pago_de_mas', 'Courier pagó de más'), value: 'courier_pago_de_mas' },
          { label: t('mismatch_types.cliente_cobro_de_mas', 'Cliente cobró de más'), value: 'cliente_cobro_de_mas' },
          {
            label: t('mismatch_types.diferencia_entre_courier_cliente', 'Diferencia courier-cliente'),
            value: 'diferencia_entre_courier_cliente',
          },
          {
            label: t('mismatch_types.ambos_incompletos_con_diferencia', 'Ambos incompletos'),
            value: 'ambos_incompletos_con_diferencia',
          },
          { label: t('mismatch_types.otro', 'Otro'), value: 'otro' },
        ],
        allOption: {
          label: t('filters.all_types', 'Todos los tipos'),
          value: 'all',
        },
        minWidth: '220px',
        maxWidth: '260px',
      },
      {
        key: 'date_from',
        label: t('filters.date_from', 'Fecha desde'),
        type: 'date',
        dateConfig: {
          placeholder: t('filters.select_date', 'Seleccionar fecha'),
        },
        minWidth: '200px',
      },
      {
        key: 'date_to',
        label: t('filters.date_to', 'Fecha hasta'),
        type: 'date',
        dateConfig: {
          placeholder: t('filters.select_date', 'Seleccionar fecha'),
        },
        minWidth: '200px',
      },
    ],
    [t, clients, isLoadingClients, shops, isLoadingShops, couriers, isLoadingCouriers],
  );

  // 🎯 Convertir valores de filtros a formato que SimpleFilter espera
  const filterValues = useMemo(() => {
    const values: Record<string, any> = {
      date_from: filters.date_from ? new Date(filters.date_from) : undefined,
      date_to: filters.date_to ? new Date(filters.date_to) : undefined,
      client_id: filters.client_id ? String(filters.client_id) : 'all',
      shop_id: filters.shop_id ? String(filters.shop_id) : 'all',
      courier_id: filters.courier_id ? String(filters.courier_id) : 'all',
      tracking_number: filters.tracking_number || '',
      mismatch_type: filters.mismatch_type || 'all',
    };
    return values;
  }, [filters]);

  // 🎯 Handler para cambios en los filtros
  const handleFilterChange = (newValues: Partial<Record<string, any>>) => {
    const updatedFilters: Partial<MismatchFilters> = {};

    // Convertir valores de vuelta al formato del modelo
    Object.keys(newValues).forEach((key) => {
      const value = newValues[key];

      if (key === 'client_id' || key === 'shop_id' || key === 'courier_id') {
        updatedFilters[key as 'client_id' | 'shop_id' | 'courier_id'] =
          value === 'all' || !value ? undefined : parseInt(value, 10);
      } else if (key === 'mismatch_type') {
        updatedFilters.mismatch_type = value === 'all' || !value ? undefined : (value as MismatchType);
      } else if (key === 'tracking_number') {
        updatedFilters.tracking_number = !value ? undefined : value;
      } else if (key === 'date_from' || key === 'date_to') {
        // Convertir Date a string formato 'YYYY-MM-DD' (equivalente a Y-m-d del API)
        if (value instanceof Date) {
          updatedFilters[key as 'date_from' | 'date_to'] = value.toLocaleDateString('en-CA'); // Produce formato YYYY-MM-DD
        } else if (value) {
          updatedFilters[key as 'date_from' | 'date_to'] = value;
        } else {
          updatedFilters[key as 'date_from' | 'date_to'] = undefined;
        }
      }
    });

    onFiltersChange(updatedFilters);
  };

  const activeFiltersCount = useMemo(
    () =>
      fields.filter((field) => {
        const value = filterValues[field.key];
        if (value === undefined || value === null || value === '') return false;
        if (field.type === 'select' && field.allOption && value === field.allOption.value) return false;
        if (field.type === 'text' && !value) return false;
        return true;
      }).length,
    [fields, filterValues],
  );

  return (
    <>
      <Search
        placeholder={t('filters.ref_placeholder', 'Buscar por referencia...')}
        value={inputValue}
        onChange={handleInputChange}
        onEnter={handleSearchSubmit}
        dataTestId="mismatches-search"
        inputDataTestId="mismatches-search-input"
      />
      <FiltersDropdown
        title={t('filters.title', 'Filtros')}
        activeFiltersCount={activeFiltersCount}
        triggerTestId="filters-toggle-button"
        dropdownContentClassName="sm:min-w-[500px] sm:max-w-[500px] p-4 bg-body-bg text-foreground border border-card-border"
      >
        <SimpleFilterContent
          fields={fields}
          values={filterValues}
          onChange={handleFilterChange}
          onReset={onResetFilters}
          showClearButton={true}
          clearButtonText={t('filters.clear', 'Limpiar')}
          translations={{
            searching: t('filters.searching', 'Buscando...'),
            selectDate: t('filters.select_date', 'Seleccionar fecha'),
            clear: t('filters.clear', 'Limpiar'),
          }}
        />
      </FiltersDropdown>
    </>
  );
};
